// JQ4-45 · US-AUDIT-005 — AuditLogPage (Org Admin)
//
// Read-only audit log viewer for org_admin role.
// Fetches GET /admin/audit-log with cursor-based pagination.
// Spec refs: R-RBAC-01 to R-RBAC-05, R-FILTER-01 to R-FILTER-06,
//            R-LIST-01 to R-LIST-06, R-DETAIL-01 to R-DETAIL-04,
//            R-EMPTY-01 to R-EMPTY-03.

import { listAuditLog } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { AuditCategory, ListAuditLogQuery } from '@jq4race/shared';
import { TriangleAlert, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { useCursorPagination } from '../../../../shared/hooks/useCursorPagination';
import { useLocationTimeZones } from '../../../../shared/hooks/useLocationTimeZones';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { isValidUuid } from '../../lib/isValidUuid';
import { AuditLogFilterBar, type AuditLogFilters } from '../components/AuditLogFilterBar';
import { AuditLogTable } from '../components/AuditLogTable';

const INITIAL_FILTERS: AuditLogFilters = {
  category: '',
  from: '',
  to: '',
  actorUserId: '',
};

const PAGE_LIMIT = 25;

export function AuditLogPage() {
  const { t } = useTranslation('audit-log');
  const [filters, setFilters] = useState<AuditLogFilters>(INITIAL_FILTERS);

  // R-LIST-06: queryKey includes all filters — changing any filter resets pagination
  const queryKey = useMemo(
    () => [
      'audit-log',
      'org',
      filters.category || null,
      filters.from || null,
      filters.to || null,
      // Only key on a valid UUID so partial actor input never triggers a 400.
      isValidUuid(filters.actorUserId) ? filters.actorUserId : null,
    ],
    [filters],
  );

  const fetchPage = useMemo(
    () => (cursor: string | null) => {
      const params: ListAuditLogQuery = {
        cursor: cursor ?? undefined,
        limit: PAGE_LIMIT,
      };
      if (filters.category) params.category = filters.category as AuditCategory;
      if (filters.from) params.from = `${filters.from}T00:00:00Z`;
      // Inclusive end-of-day with sub-second window so forensic events are not dropped.
      if (filters.to) params.to = `${filters.to}T23:59:59.999Z`;
      if (filters.actorUserId && isValidUuid(filters.actorUserId))
        params.actorUserId = filters.actorUserId;
      return listAuditLog(params);
    },
    [filters],
  );

  const { activeTimeZone } = useLocationTimeZones();
  const { items, hasMore, isLoading, isFetchingNextPage, fetchNextPage, error } =
    useCursorPagination({
      queryKey,
      fetchPage,
    });

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.title')}
        description={t('page.orgDescription')}
      />

      {/* Error banner */}
      {error && <ErrorBanner message={translateApiError(error, t)} />}

      {/* Filter bar */}
      <AuditLogFilterBar filters={filters} onFiltersChange={setFilters} />

      {/* Results table */}
      <AuditLogTable
        items={items}
        isLoading={isLoading}
        hasMore={hasMore}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={fetchNextPage}
        timeZone={activeTimeZone}
      />
    </div>
  );
}

// ── Error banner ──────────────────────────────────────────────────────────────

function ErrorBanner({ message }: { message: string }) {
  const { t } = useTranslation('audit-log');
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={t('errors.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
